// useChannelAttachmentNodes.js

import { useEffect, useMemo, useState } from 'react';

// Hooks
import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';

const getAttachmentNodeKey = (attachment) => {
  const explicitNodeKey = typeof attachment?.nodeKey === 'string' ? attachment.nodeKey.trim() : '';
  if (explicitNodeKey) return explicitNodeKey;

  return '';
};

const collectAttachmentNodeKeys = (messages = []) => {
  const keys = new Set();

  (Array.isArray(messages) ? messages : []).forEach((message) => {
    (Array.isArray(message?.attachments) ? message.attachments : []).forEach((attachment) => {
      const typeKey = String(attachment?.type || '').trim().toLowerCase();
      if (typeKey && typeKey !== 'node_ref') return;

      const nodeKey = getAttachmentNodeKey(attachment);
      if (nodeKey) keys.add(nodeKey);
    });
  });

  return Array.from(keys);
};

const hasPhotoMedia = (node) => {
  return Boolean(
    node?.url_feed
    || node?.url_grid
    || node?.url_thumb
    || node?.url
    || node?.storagePath
  );
};

const hasVideoMedia = (node) => {
  return Boolean(
    node?.url
    || node?.storagePath
    || node?.data_video?.url
    || node?.data_video?.storagePath
    || node?.coverUrl
    || node?.coverStoragePath
  );
};

const deriveNodeStatus = (node) => {
  if (!node?.key) return 'unavailable';

  const typeKey = String(node?.type || '').toUpperCase();
  if (typeKey === 'PHOTO' && !hasPhotoMedia(node)) return 'loading';
  if (typeKey === 'VIDEO' && !hasVideoMedia(node)) return 'loading';

  return 'ready';
};

/**
 * useChannelAttachmentNodes
 *
 * Batches node fetches for node reference attachments in channel messages.
 * Missing or unreadable references are marked unavailable for safe rendering.
 *
 * @param {object} options - Hook options.
 * @param {array} options.messages - Channel messages.
 *
 * @returns {object} Attachment node status map.
 */
const useChannelAttachmentNodes = ({ messages }) => {
  const [attachmentNodesByKey, setAttachmentNodesByKey] = useState({});

  const requiredNodeKeys = useMemo(() => {
    return collectAttachmentNodeKeys(messages);
  }, [messages]);

  const {
    nodesByKey,
    isLoading,
    isFetching,
  } = useNodesByKeysQuery(requiredNodeKeys, {
    enabled: requiredNodeKeys.length > 0,
  });

  useEffect(() => {
    if (!requiredNodeKeys.length) {
      setAttachmentNodesByKey((prev) => (
        Object.keys(prev).length === 0 ? prev : {}
      ));
      return;
    }

    const isPending = isLoading || isFetching;

    setAttachmentNodesByKey((prev) => {
      const next = {};

      requiredNodeKeys.forEach((nodeKey) => {
        const node = nodesByKey.get(nodeKey) || null;

        if (node) {
          next[nodeKey] = {
            status: deriveNodeStatus(node),
            node,
          };
          return;
        }

        if (prev[nodeKey]?.node) {
          next[nodeKey] = prev[nodeKey];
          return;
        }

        next[nodeKey] = {
          status: isPending ? 'loading' : 'unavailable',
          node: null,
        };
      });

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every((key) => {
          const prevEntry = prev[key];
          const nextEntry = next[key];
          return prevEntry?.status === nextEntry?.status
            && prevEntry?.node === nextEntry?.node;
        })
      ) {
        return prev;
      }

      return next;
    });
  }, [requiredNodeKeys, nodesByKey, isLoading, isFetching]);

  return {
    attachmentNodesByKey,
  };
};

export default useChannelAttachmentNodes;
